<script>
import debounce from 'lodash/debounce';

const PLAN_RANK = { personal: 0, business: 1, agency: 2 };

export default {
  name: 'AdminMergeDialog',
  props: {
    // The owner row (from /admin/users) of the account that will be kept
    primary: Object,
    visible: Boolean
  },
  data() {
    return {
      step: 'select',
      search: '',
      results: [],
      isSearching: false,
      secondary: null,
      password: '',
      isMerging: false
    };
  },
  computed: {
    cards() {
      return [
        { kind: 'keep', label: 'Primary account (kept)', user: this.primary },
        { kind: 'delete', label: 'Merged account (deleted)', user: this.secondary }
      ].filter(card => card.user);
    },

    // True when the account being merged in is on a higher plan than the primary. The merged account
    // keeps the primary's plan, so the review step warns that the higher plan's features don't carry over.
    isDowngrade() {
      return (
        !!this.primary &&
        !!this.secondary &&
        PLAN_RANK[this.secondary.account.plan] > PLAN_RANK[this.primary.account.plan]
      );
    }
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.step = 'select';
        this.search = '';
        this.results = [];
        this.secondary = null;
        this.password = '';
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      }
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false);
    },

    handleSearch: debounce(function () {
      const search = this.search.trim().replace(/^mailto:/, '');
      if (!search) {
        this.results = [];
        return;
      }

      this.isSearching = true;
      this.$http
        .get('/admin/users', { params: { search, sortBy: 'role' } })
        .then(res => {
          this.results = res.data.users.filter(user => user.isOwner && user.accountId !== this.primary.accountId);
        })
        .catch(err => {
          this.$notify({ type: 'error', title: this.$t('Error'), message: err.response.data.message });
        })
        .finally(() => (this.isSearching = false));
    }, 250),

    // Only owners are listed so there is one row per account
    select(user) {
      this.secondary = user;
      this.step = 'review';
      this.$nextTick(() => this.$refs.password && this.$refs.password.focus());
    },

    merge() {
      this.isMerging = true;
      this.$http
        .post(`/admin/accounts/${this.primary.accountId}/merge`, {
          secondaryId: this.secondary.accountId,
          password: this.password
        })
        .then(res => {
          this.$notify({ type: 'success', title: 'Accounts Merged', message: res.data.message });
          this.$emit('merged', this.secondary.accountId);
          this.close();
        })
        .catch(err => {
          this.$notify({ type: 'error', title: this.$t('Error'), message: err.response.data.message });
        })
        .finally(() => (this.isMerging = false));
    }
  }
};
</script>

<template>
  <el-dialog title="Merge Accounts" :visible="visible" width="40rem" append-to-body @close="close">
    <div v-if="primary" class="merge-dialog">
      <!-- Account cards -->
      <div class="cards">
        <div v-for="card in cards" :key="card.kind" :class="['card', `card-${card.kind}`]">
          <div class="card-label">{{ card.label }}</div>
          <strong>{{ card.user.name }}</strong>
          <span :class="['plan', `plan-${card.user.account.plan}`]">{{ card.user.account.plan }}</span>
          <br />
          <small>
            {{ card.user.email }}<br />
            <span class="text-muted"><i class="el-icon-fa-hashtag" />{{ card.user.accountId }}</span>
          </small>
        </div>
      </div>

      <!-- Step 1: pick the account to merge in -->
      <div v-if="step === 'select'">
        <p class="intro">
          Search for the owner of the account to merge into the primary account. That account will be deleted.
        </p>
        <el-input
          ref="search"
          v-model="search"
          :prefix-icon="isSearching ? 'el-icon-fa-circle-notch el-icon-fa-spin' : 'el-icon-fa-search'"
          placeholder="Search by name or email"
          clearable
          @input="handleSearch"
        />
        <div class="results">
          <ul v-if="results.length">
            <li v-for="user in results" :key="user.id" @click="select(user)">
              <strong>{{ user.name }}</strong>
              <span :class="['plan', `plan-${user.account.plan}`]">{{ user.account.plan }}</span>
              <br />
              <small>{{ user.email }}</small>
            </li>
          </ul>
          <div v-else class="results-empty">
            {{ search && !isSearching ? 'No owners found' : 'Results will appear here' }}
          </div>
        </div>
      </div>

      <!-- Step 2: review and confirm -->
      <div v-else>
        <ul class="summary">
          <li>
            All users, sites, and permissions move to <strong>{{ primary.name }}’s</strong> account.
          </li>
          <li>{{ secondary.name }} becomes an admin.</li>
          <li>Sites with the same URL in both accounts keep {{ primary.name }}’s copy.</li>
          <li>{{ secondary.name }}’s subscription is cancelled at the end of the billing period.</li>
          <li v-if="isDowngrade">
            <strong>
              The merged account stays on the {{ primary.account.plan }} plan. {{ secondary.account.plan }} features
              (custom domain, white-label branding, API logins) do not carry over.
            </strong>
          </li>
          <li>
            <strong>{{ secondary.name }}’s account is permanently deleted.</strong>
          </li>
        </ul>
        <el-input
          ref="password"
          v-model="password"
          type="password"
          placeholder="Enter your password to confirm"
          @keyup.enter.native="merge"
        />
      </div>
    </div>

    <template slot="footer">
      <el-button v-if="step === 'review'" :disabled="isMerging" @click="step = 'select'">Back</el-button>
      <el-button :disabled="isMerging" @click="close">Cancel</el-button>
      <el-button v-if="step === 'review'" type="danger" :loading="isMerging" :disabled="!password" @click="merge">
        Merge Accounts
      </el-button>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.cards {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @include breakpoint('sm-and-down') {
    flex-direction: column;
  }
}

.card {
  flex: 1;
  border: 2px solid $--color-gray-20;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
  line-height: 1.5;

  &.card-keep {
    border-color: $--color-success;
  }

  &.card-delete {
    border-color: $--color-danger;
  }
}

.card-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;

  .card-keep & {
    color: $--color-success;
  }

  .card-delete & {
    color: $--color-danger;
  }
}

.intro {
  margin: 0 0 1rem;
  line-height: 1.5;
}

.results {
  height: 16rem;
  margin-top: 1rem;
  overflow-y: auto;
  border: 1px solid $--color-gray-20;
  border-radius: 0.25rem;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: 0.5rem 1rem;
    cursor: pointer;
    border-bottom: 1px solid $--color-gray-20;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: $--color-gray-10;
    }
  }
}

.results-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: $--color-gray-40;
}

.summary {
  margin: 0 0 1.5rem;
  padding-left: 1.25rem;
  line-height: 1.5;

  li {
    margin-bottom: 0.5rem;
  }
}

.plan {
  display: inline-block;
  border-radius: 0.25rem;
  color: $--color-white;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  background-color: $--color-gray-60;
  padding: 0.125rem 0.25rem;
  vertical-align: middle;
  margin-left: 0.25rem;

  &.plan-personal {
    background-color: $--color-warning;
  }

  &.plan-business {
    background-color: $--color-success;
  }

  &.plan-agency {
    background-color: $--color-primary;
  }
}
</style>
